import { forwardRef, useEffect, useRef } from 'react';
import { Dialog } from '@base-ui/react/dialog';
import useAnimatedPresence from '../../../hooks/useAnimatedPresence';

function joinClasses(...values) {
  return values.filter(Boolean).join(' ');
}

function resolveFinalFocusTarget(finalFocus, fallbackElement) {
  if (finalFocus === false) {
    return null;
  }

  if (finalFocus && typeof finalFocus === 'object' && 'current' in finalFocus) {
    return finalFocus.current ?? null;
  }

  return fallbackElement ?? null;
}

const BaseDialogTitle = forwardRef(function BaseDialogTitle({ className = '', ...props }, ref) {
  return (
    <Dialog.Title
      ref={ref}
      className={joinClasses('base-dialog__title', className)}
      {...props}
    />
  );
});

const BaseDialogDescription = forwardRef(function BaseDialogDescription(
  { className = '', ...props },
  ref,
) {
  return (
    <Dialog.Description
      ref={ref}
      className={joinClasses('base-dialog__description', className)}
      {...props}
    />
  );
});

function BaseDialog({
  open,
  onClose,
  children,
  size = 'sm',
  modal = true,
  overlayTone = 'heavy',
  dismissible = true,
  closeOnOutsideClick = true,
  initialFocus = true,
  finalFocus = true,
  popupClassName = '',
  backdropClassName = '',
  ariaLabel,
  closeLabel = 'Close dialog',
}) {
  const { mounted, stage, ref } = useAnimatedPresence(open);
  const wasOpenRef = useRef(open);
  const previouslyFocusedElementRef = useRef(null);
  const shouldRestoreFocusRef = useRef(false);

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      previouslyFocusedElementRef.current =
        document.activeElement instanceof Element ? document.activeElement : null;
      shouldRestoreFocusRef.current = finalFocus !== false;
    } else if (!open && wasOpenRef.current && finalFocus === false) {
      shouldRestoreFocusRef.current = false;
    }

    wasOpenRef.current = open;
  }, [finalFocus, open]);

  useEffect(() => {
    if (mounted || !shouldRestoreFocusRef.current) {
      return undefined;
    }

    shouldRestoreFocusRef.current = false;
    const focusTarget = resolveFinalFocusTarget(finalFocus, previouslyFocusedElementRef.current);

    if (!focusTarget || typeof focusTarget.focus !== 'function' || focusTarget.isConnected === false) {
      return undefined;
    }

    const frame = requestAnimationFrame(() => {
      focusTarget.focus();
    });

    return () => cancelAnimationFrame(frame);
  }, [finalFocus, mounted]);

  if (!mounted) {
    return null;
  }

  const dialogOpen = open || stage === 'exiting';

  return (
    <Dialog.Root
      modal={modal}
      open={dialogOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen && open && dismissible) {
          onClose?.();
        }
      }}
      disablePointerDismissal
    >
      <Dialog.Portal keepMounted>
        <Dialog.Backdrop
          ref={ref}
          className={joinClasses('base-dialog__backdrop', backdropClassName)}
          data-stage={stage}
          data-tone={overlayTone}
          onClick={(event) => {
            if (dismissible && closeOnOutsideClick && event.target === event.currentTarget) {
              onClose?.();
            }
          }}
        />
        <Dialog.Popup
          className={joinClasses('base-dialog__popup', `base-dialog__popup--${size}`, popupClassName)}
          data-stage={stage}
          initialFocus={initialFocus}
          aria-label={ariaLabel}
        >
          {dismissible ? (
            <Dialog.Close className="base-dialog__sr-close">
              {closeLabel}
            </Dialog.Close>
          ) : null}
          {children}
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

BaseDialog.Title = BaseDialogTitle;
BaseDialog.Description = BaseDialogDescription;

export default BaseDialog;
